import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useMotionBudget } from '../../../app/hooks/useMotionBudget';
const SPARKLE_COLOR_MAP: Record<string, string> = {
  amber: 'bg-amber-400',
  purple: 'bg-purple-400',
  fuchsia: 'bg-fuchsia-400'
};
/**
 * SparkleParticles — floating motes for rare/legendary cards.
 * Positions are computed once per mount (stable across re-renders).
 * Suppressed entirely when the user prefers reduced motion.
 */
export function SparkleParticles({
  count = 6,
  color = 'amber'
}: {
  count?: number;
  color?: string;
}) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const positions = useMemo(() => Array.from({
    length: count
  }).map(() => ({
    left: `${15 + Math.random() * 70}%`,
    top: `${15 + Math.random() * 70}%`
  })), [count]);
  if (!shouldAnimate) return null;
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-id="element-1814" data-mp="18zk2bvgtck17">
      {positions.map((pos, i) => <motion.div key={i} className={`absolute w-1.5 h-1.5 rounded-full ${SPARKLE_COLOR_MAP[color] || SPARKLE_COLOR_MAP.amber}`} style={pos} animate={{
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      y: [0, -10, -20]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: i * 0.3,
      ease: 'easeOut'
    }} data-id="element-1815" data-mp="1c93ysy90465m" data-mpi={i} />)}
    </div>;
}
/** GoldenShimmer — sweeping highlight band for rare achievements. */
export function GoldenShimmer() {
  const {
    shouldAnimate
  } = useMotionBudget();
  if (!shouldAnimate) {
    // Static warm tint preserves the "special" feel without motion.
    return <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none bg-amber-200/15" aria-hidden="true" data-id="element-1816" data-mp="29t6vlf4acfcf" />;
  }
  return <motion.div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none" aria-hidden="true" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} data-id="element-1817" data-mp="fi1wmo458r3f">
      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/40 to-transparent" animate={{
      x: ['-100%', '200%']
    }} transition={{
      duration: 3,
      repeat: Infinity,
      repeatDelay: 2,
      ease: 'easeInOut'
    }} data-id="element-1818" data-mp="3hli60v3b0y9" />
    </motion.div>;
}
/** LegendaryGlow — pulsing aura behind legendary cards. */
export function LegendaryGlow() {
  const {
    shouldAnimate
  } = useMotionBudget();
  if (!shouldAnimate) {
    // Static glow ring — keeps the legendary distinction, drops the pulse.
    return <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-25 blur-md pointer-events-none" aria-hidden="true" data-id="element-1819" data-mp="v8rdm3zv8ah7" />;
  }
  return <motion.div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 opacity-30 blur-md pointer-events-none" aria-hidden="true" animate={{
    opacity: [0.2, 0.4, 0.2],
    scale: [1, 1.02, 1]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-1820" data-mp="1oqnuok2hlg9b" />;
}